import { HttpClient, HttpHeaders } from '@angular/common/http';

const RETRYABLE_MESSAGE_FRAGMENTS = [
  'upstream connect error',
  'disconnect/reset before headers',
  'remote connection failure',
  'connection reset',
  'econnreset',
  'etimedout',
  'econnrefused',
  'socket hang up',
  'network error',
  'gateway timeout',
  'bad gateway',
  'service unavailable',
  'temporarily unavailable',
  'failed to process request',
];

const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

/** Default POST attempts (initial try + one retry) for /legacy/get-azure-token. */
export const SCYTALE_KYC_MAX_ATTEMPTS = 2;

function extractHttpStatusFromUnknown(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  const status = (err as { status?: number }).status;
  if (typeof status === 'number' && status >= 100 && status < 600) {
    return status;
  }
  const raw = extractScytaleKycErrorMessage(err);
  const fromWrapper = raw.match(/Http failure response for .+: (\d{3})\b/i);
  if (fromWrapper) {
    return parseInt(fromWrapper[1], 10);
  }
  const fromHttp = raw.match(/^HTTP (\d{3})\b/i);
  if (fromHttp) {
    return parseInt(fromHttp[1], 10);
  }
  return undefined;
}

/** Map API / network noise to short copy for trustees (logs keep raw detail). */
export function humanizeScytaleKycUserMessage(
  rawMessage: string,
  httpStatus?: number
): string {
  const raw = String(rawMessage ?? '').trim();
  if (!raw) {
    return 'Unable to start Live ID verification. Please try again.';
  }

  const lower = raw.toLowerCase();
  const status =
    httpStatus ??
    (() => {
      const w = raw.match(/Http failure response for .+: (\d{3})\b/i);
      if (w) {
        return parseInt(w[1], 10);
      }
      const h = raw.match(/^HTTP (\d{3})\b/i);
      return h ? parseInt(h[1], 10) : undefined;
    })();

  if (isHttpClientWrapperMessage(raw)) {
    return messageForScytaleHttpStatus(status);
  }

  if (lower.includes('scytale kyc request timed out') || lower.includes('timed out')) {
    return 'Live ID verification timed out. Check your connection and try Initiate Recovery again.';
  }

  if (RETRYABLE_MESSAGE_FRAGMENTS.some((f) => lower.includes(f))) {
    return 'Temporary issue reaching the verification service. Wait a moment — we will retry automatically.';
  }

  if (
    lower.includes('missing toughkey') ||
    lower.includes('recovery session token') ||
    lower.includes('missing recovery session')
  ) {
    return 'Secure Shell has not finished preparing recovery. Complete PIN and initiate in the desktop app, then try again.';
  }

  if (lower.includes('missing deceased owner') || lower.includes('missing secret enrollment')) {
    return 'Recovery details are incomplete. Refresh the page and start Initiate Delegation again.';
  }

  if (status === 401 || lower.includes('authentication failed') || lower.includes('unauthorized')) {
    return 'Your session expired. Sign in again, then try Initiate Recovery.';
  }

  if (status === 403 || lower.includes('forbidden') || lower.includes('not allowed')) {
    return 'Live ID verification is not permitted for this recovery request. Contact support if this continues.';
  }

  if (status === 404) {
    return 'Verification service is unavailable. Try again later or contact support.';
  }

  if (status != null && RETRYABLE_HTTP_STATUSES.has(status)) {
    return messageForScytaleHttpStatus(status);
  }

  if (/^http \d{3}\b/i.test(raw)) {
    return messageForScytaleHttpStatus(status);
  }

  const looksTechnical =
    raw.length > 200 ||
    /https?:\/\//i.test(raw) ||
    /\bat\s+\w+\./i.test(raw) ||
    raw.includes('{"') ||
    raw.includes('Sql') ||
    lower.includes('exception');

  if (looksTechnical) {
    return 'Unable to start Live ID verification. Refresh the page and try Initiate Recovery again.';
  }

  return raw;
}

function messageForScytaleHttpStatus(status?: number): string {
  switch (status) {
    case 401:
      return 'Your session expired. Sign in again, then try Initiate Recovery.';
    case 403:
      return 'Live ID verification is not permitted for this recovery request. Contact support if this continues.';
    case 404:
      return 'Verification service is unavailable. Try again later or contact support.';
    case 408:
    case 429:
      return 'Verification service is busy. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Verification service is temporarily unavailable. Try Initiate Recovery again in a few minutes.';
    default:
      return 'We could not start Live ID verification. Check your connection and try Initiate Recovery again.';
  }
}

/** User-facing copy when trustee disbursement Scytale launch budget is exhausted. */
export function formatScytaleKycAttemptsExhaustedMessage(
  detail?: string,
  maxAttempts: number = SCYTALE_KYC_MAX_ATTEMPTS
): string {
  const prefix = `We could not complete Live ID verification after ${maxAttempts} tries`;
  const fallback = `${prefix}. Refresh the page, then use Initiate Delegation or Initiate Recovery again.`;
  const trimmed = String(detail ?? '').trim();
  const friendly = trimmed ? humanizeScytaleKycUserMessage(trimmed) : '';
  if (!friendly) {
    return fallback;
  }
  const friendlyLower = friendly.toLowerCase();
  if (friendlyLower.startsWith(prefix.toLowerCase())) {
    return friendly;
  }
  if (/after \d+ attempts?/i.test(friendly) || /after \d+ tries?/i.test(friendly)) {
    return friendly;
  }
  return `${prefix}. ${friendly}`;
}

export interface ScytaleKycRetryOptions {
  params?: Record<string, string>;
  /** When true (default), AuthInterceptor skips the global app loader. */
  skipGlobalLoader?: boolean;
  /** Total attempts including the first (default {@link SCYTALE_KYC_MAX_ATTEMPTS}). */
  maxAttempts?: number;
  /** Base delay before retry 2 (default 2000ms), then exponential backoff. */
  baseDelayMs?: number;
  /** Optional delay before the first POST (default 0). */
  initialDelayMs?: number;
  logTag?: string;
  /** Called before each retry wait (attempt is 1-based index of the failed try). */
  onRetry?: (attempt: number, maxAttempts: number, delayMs: number, message: string) => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shellEscapeSingleQuoted(value: string): string {
  return value.replace(/'/g, `'\"'\"'`);
}

/** Copy/paste curl for POST /legacy/get-azure-token (self-kyc or recovery). */
export function formatScytaleKycPostCurl(
  url: string,
  body: Record<string, unknown>,
  options?: {
    queryParams?: Record<string, string>;
    authToken?: string | null;
  }
): string {
  const token = options?.authToken ?? localStorage.getItem('token') ?? '<TOKEN>';
  const qs =
    options?.queryParams && Object.keys(options.queryParams).length
      ? `?${new URLSearchParams(options.queryParams).toString()}`
      : '';
  const payload = JSON.stringify(body);
  return [
    `# POST ${url}${qs}`,
    `curl -sS -X POST "${url}${qs}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    `  --data-raw '${shellEscapeSingleQuoted(payload)}'`,
  ].join('\n');
}

/** Copy/paste curl for trustee-kyc status GET. */
export function formatHttpGetCurl(
  url: string,
  queryParams?: Record<string, string>,
  authToken?: string | null
): string {
  const token = authToken ?? localStorage.getItem('token') ?? '<TOKEN>';
  const qs =
    queryParams && Object.keys(queryParams).length
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
  return [
    `# GET ${url}${qs}`,
    `curl -sS -X GET "${url}${qs}" \\`,
    `  -H "Authorization: Bearer ${token}"`,
  ].join('\n');
}

export function logScytaleKycCurlToConsole(
  tag: string,
  curl: string,
  meta?: Record<string, unknown>
): void {
  console.warn(`${tag} — copy/paste curl below`);
  console.log(curl);
  if (meta) {
    console.log(`${tag} — meta`, meta);
  }
}

function isHttpClientWrapperMessage(value: string): boolean {
  return /^Http failure response for /i.test(value.trim());
}

/** Extract human-readable error text from HttpClient error or API body. */
export function extractScytaleKycErrorMessage(err: unknown): string {
  if (err == null) {
    return '';
  }
  if (typeof err === 'string') {
    return err;
  }
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const httpErr = err as { error?: unknown; status?: number; statusText?: string; message?: string };

    if (o['success'] === false && typeof o['message'] === 'string' && o['message'].trim()) {
      return o['message'].trim();
    }

    if (httpErr.error && typeof httpErr.error === 'object') {
      const body = httpErr.error as Record<string, unknown>;
      const nested = [body['message'], body['error']].find(
        (v) => typeof v === 'string' && v.trim()
      );
      if (typeof nested === 'string') {
        return nested.trim();
      }
    }
    if (typeof httpErr.error === 'string' && httpErr.error.trim()) {
      return httpErr.error.trim();
    }

    const topLevelMessage =
      typeof o['message'] === 'string' ? o['message'].trim() : '';
    if (topLevelMessage && !isHttpClientWrapperMessage(topLevelMessage)) {
      return topLevelMessage;
    }

    if (typeof o['error'] === 'string' && o['error'].trim()) {
      return o['error'].trim();
    }

    if (httpErr.status != null) {
      return `HTTP ${httpErr.status}${httpErr.statusText ? ` ${httpErr.statusText}` : ''}`;
    }
  }
  return String(err);
}

function asScytaleKycErrorRecord(err: unknown): Record<string, unknown> | null {
  if (!err || typeof err !== 'object') {
    return null;
  }
  const root = err as Record<string, unknown>;
  const httpBody =
    root['error'] && typeof root['error'] === 'object'
      ? (root['error'] as Record<string, unknown>)
      : null;
  if (root['success'] === false || httpBody?.['success'] === false) {
    return httpBody ?? root;
  }
  if (httpBody && (httpBody['message'] != null || httpBody['limit'] != null)) {
    return httpBody;
  }
  return root;
}

/** True when get-azure-token failed due to exhausted KYC credits (non-retryable). */
export function isScytaleKycCreditLimitError(err: unknown): boolean {
  const body = asScytaleKycErrorRecord(err);
  const message = extractScytaleKycErrorMessage(err).toLowerCase();
  if (
    message.includes('kyc verification limit') ||
    message.includes('kyc credits') ||
    message.includes('upgrade your plan')
  ) {
    return true;
  }
  if (!body) {
    return false;
  }
  const remaining = body['remaining'];
  const available = body['available'];
  const limit = body['limit'];
  if (remaining === 0 || available === 0) {
    const msg = String(body['message'] ?? '').toLowerCase();
    return msg.includes('limit') || msg.includes('credit') || limit != null;
  }
  return false;
}

/** User-facing message for Scytale KYC errors (includes credit limit details). */
export function formatScytaleKycUserMessage(
  err: unknown,
  options?: { subscriptionPath?: string }
): string {
  const subscriptionPath = options?.subscriptionPath ?? '/profile/subscription';
  const body = asScytaleKycErrorRecord(err);
  const raw =
    extractScytaleKycErrorMessage(err) || 'Unable to start KYC verification.';
  const httpStatus = extractHttpStatusFromUnknown(err);

  if (!isScytaleKycCreditLimitError(err)) {
    return humanizeScytaleKycUserMessage(raw, httpStatus);
  }

  const message = humanizeScytaleKycUserMessage(raw, httpStatus);

  const limit = body?.['limit'];
  const used = body?.['used'];
  const remaining = body?.['remaining'] ?? body?.['available'];
  const usageParts: string[] = [];
  if (used != null && used !== '') {
    usageParts.push(`${used} used`);
  }
  if (remaining != null && remaining !== '') {
    usageParts.push(`${remaining} remaining`);
  }
  if (limit != null && limit !== '') {
    usageParts.push(`limit ${limit}`);
  }
  const usageSuffix = usageParts.length ? ` (${usageParts.join(', ')})` : '';

  return (
    `${message}${usageSuffix} ` +
    `Go to Profile → Subscription to buy more KYC credits or upgrade your plan.`
  );
}

/** True for transient gateway / Scytale upstream failures (manual retry often succeeds). */
export function isRetryableScytaleKycError(err: unknown): boolean {
  if (isScytaleKycCreditLimitError(err)) {
    return false;
  }
  const httpErr = err as { status?: number } | null;
  if (httpErr?.status != null && RETRYABLE_HTTP_STATUSES.has(httpErr.status)) {
    return true;
  }
  const msg = extractScytaleKycErrorMessage(err).toLowerCase();
  if (!msg) {
    return false;
  }
  return RETRYABLE_MESSAGE_FRAGMENTS.some((fragment) => msg.includes(fragment));
}

function isRetryableScytaleKycResponse(res: Record<string, unknown> | null | undefined): boolean {
  if (!res || res['success'] !== false) {
    return false;
  }
  return isRetryableScytaleKycError(res);
}

/**
 * POST /legacy/get-azure-token with exponential backoff on upstream / gateway failures.
 * Handles both HTTP errors and success:false bodies (dev API often returns 200 + success:false).
 */
export async function postScytaleKycWithRetry(
  http: HttpClient,
  url: string,
  body: Record<string, unknown>,
  options?: ScytaleKycRetryOptions
): Promise<Record<string, unknown>> {
  const maxAttempts = Math.max(1, options?.maxAttempts ?? SCYTALE_KYC_MAX_ATTEMPTS);
  const baseDelayMs = options?.baseDelayMs ?? 2000;
  const tag = options?.logTag ?? '[ScytaleKYC]';
  const skipGlobalLoader = options?.skipGlobalLoader !== false;
  const requestOptions = {
    params: options?.params,
    ...(skipGlobalLoader
      ? { headers: new HttpHeaders({ skipLoader: 'true' }) }
      : {}),
  };

  if (options?.initialDelayMs && options.initialDelayMs > 0) {
    await sleep(options.initialDelayMs);
  }

  let lastErr: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = (await http
        .post<Record<string, unknown>>(url, body, requestOptions)
        .toPromise()) as Record<string, unknown>;

      if (isRetryableScytaleKycResponse(res)) {
        lastErr = res;
        if (attempt < maxAttempts) {
          const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
          const message = extractScytaleKycErrorMessage(res);
          console.warn(`${tag} upstream error (success=false), retry ${attempt}/${maxAttempts} in ${delayMs}ms`, {
            message,
          });
          options?.onRetry?.(attempt, maxAttempts, delayMs, message);
          await sleep(delayMs);
          continue;
        }
        return res;
      }

      if (attempt > 1) {
        console.log(`${tag} succeeded on attempt ${attempt}/${maxAttempts}`);
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (attempt < maxAttempts && isRetryableScytaleKycError(err)) {
        const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
        const message = extractScytaleKycErrorMessage(err);
        console.warn(`${tag} HTTP error, retry ${attempt}/${maxAttempts} in ${delayMs}ms`, {
          message,
        });
        options?.onRetry?.(attempt, maxAttempts, delayMs, message);
        await sleep(delayMs);
        continue;
      }
      throw err;
    }
  }

  if (lastErr && typeof lastErr === 'object' && !('status' in (lastErr as object))) {
    return lastErr as Record<string, unknown>;
  }
  throw lastErr;
}
